
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Image, AlertCircle, Trash2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { canUseCustomDomain, canUseAIChat } from "@/utils/planLimits";
import { UpgradePrompt } from "@/components/shared/UpgradePrompt";

type CompanySettingsProps = {
  company: {
    id: string;
    name: string;
    logo_url: string | null;
    plan: string;
    custom_domain: string | null;
    ai_support_enabled?: boolean;
  };
  onUpdate: () => void;
};

type FormValues = {
  name: string;
  logo: FileList;
  custom_domain?: string;
  ai_support_enabled?: boolean;
};

const CompanySettings = ({ company, onUpdate }: CompanySettingsProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");

  const form = useForm<FormValues>({
    defaultValues: {
      name: company.name,
      custom_domain: company.custom_domain || "",
      ai_support_enabled: company.ai_support_enabled || false,
    },
  });

  // Check if the current plan allows custom domains (starter or enterprise)
  const allowsCustomDomain =
    company.plan === "starter" || company.plan === "enterprise";

  const onSubmit = async (data: FormValues) => {
    try {
      // Check custom domain permission
      if (data.custom_domain && data.custom_domain !== company.custom_domain) {
        const domainCheck = canUseCustomDomain(company.plan);
        if (!domainCheck.allowed) {
          setUpgradeMessage(domainCheck.message || "");
          setShowUpgradePrompt(true);
          return;
        }
      }

      // Check AI support permission
      if (data.ai_support_enabled && !company.ai_support_enabled) {
        const aiCheck = canUseAIChat(company.plan);
        if (!aiCheck.allowed) {
          setUpgradeMessage(aiCheck.message || "");
          setShowUpgradePrompt(true);
          return;
        }
      }

      setIsLoading(true);

      let logo_url = company.logo_url;

      // Handle logo upload if a new file was selected
      if (data.logo?.[0]) {
        const file = data.logo[0];
        const fileExt = file.name.split(".").pop();
        const filePath = `${company.id}-logo.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("product-images")
          .upload(filePath, file, {
            upsert: true,
          });

        if (uploadError) {
          throw uploadError;
        }

        const {
          data: { publicUrl },
        } = supabase.storage.from("product-images").getPublicUrl(filePath);

        logo_url = publicUrl;
      }

      // If custom domain is being updated, configure it with Netlify
      if (data.custom_domain !== company.custom_domain) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("No session found");

        const { error: netlifyError } = await supabase.functions.invoke(
          "configure-custom-domain",
          {
            body: { 
              customDomain: data.custom_domain,
              currentDomain: company.custom_domain // Pass the current domain for removal
            },
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          }
        );

        if (netlifyError) {
          throw new Error(
            `Failed to configure domain: ${netlifyError.message}`
          );
        }
      }

      // Update company details
      const { error: updateError } = await supabase
        .from("companies")
        .update({
          name: data.name,
          logo_url,
          custom_domain: data.custom_domain || null,
          ai_support_enabled: data.ai_support_enabled,
        })
        .eq("id", company.id);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Company details updated successfully",
      });

      onUpdate();
    } catch (error) {
      console.error("Error updating company:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update company details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCompany = async () => {
    try {
      setIsDeleting(true);

      const { data, error } = await supabase.rpc('delete_company_and_user', {
        p_company_id: company.id
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string };

      if (result && !result.success) {
        throw new Error(result.error || 'Failed to delete company');
      }

      toast({
        title: "Company Deleted",
        description: "Your company and account have been permanently deleted.",
      });

      // Sign out and redirect to home
      await supabase.auth.signOut();
      navigate('/');
    } catch (error: any) {
      console.error("Error deleting company:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete company",
        variant: "destructive",
      });
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Company Settings</h2>
        <p className="text-muted-foreground">Update your company details</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="logo"
            render={({ field: { onChange, value, ...field } }) => (
              <FormItem>
                <FormLabel>Company Logo</FormLabel>
                <FormControl>
                  <div className="flex items-start gap-4">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => onChange(e.target.files)}
                      {...field}
                      className="flex-1"
                    />
                    {company.logo_url ? (
                      <img
                        src={company.logo_url}
                        alt="Company logo"
                        className="w-32 h-32 object-contain border rounded-lg"
                      />
                    ) : (
                      <div className="flex items-center justify-center w-32 h-32 border-2 border-dashed rounded-lg">
                        <Image className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* New AI Support Toggle - Only show for Starter and Enterprise plans */}
          {(company.plan === 'starter' || company.plan === 'enterprise') && (
            <FormField
              control={form.control}
              name="ai_support_enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">AI Chat Support</FormLabel>
                    <FormDescription>
                      Enable AI to automatically respond to customer chat messages based on your knowledge base articles.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}

          {allowsCustomDomain && (
            <FormField
              control={form.control}
              name="custom_domain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom Domain</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="yourdomain.com" />
                  </FormControl>
                  <FormDescription>
                    Enter your domain without any prefix
                  </FormDescription>
                  <FormMessage />
                  {field.value && (
                    <Alert className="mt-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>DNS Configuration Required</AlertTitle>
                      <AlertDescription className="mt-2">
                        <p>
                          To set up your custom domain, add the following DNS
                          records to your domain:
                        </p>
                        <div className="mt-2 p-4 bg-muted rounded-lg">
                          <p className="font-semibold mb-2">For Feedback Portal:</p>
                          <p>
                            <strong>Type:</strong> CNAME
                          </p>
                          <p>
                            <strong>Name:</strong> feedback
                          </p>
                          <p>
                            <strong>Value:</strong>{" "}
                            whitelabel.feednatic.com
                          </p>
                          <p>
                            <strong>TTL:</strong> 3600
                          </p>
                        </div>
                        
                        <div className="mt-4 p-4 bg-muted rounded-lg">
                          <p className="font-semibold mb-2">For Help Desk:</p>
                          <p>
                            <strong>Type:</strong> CNAME
                          </p>
                          <p>
                            <strong>Name:</strong> help
                          </p>
                          <p>
                            <strong>Value:</strong>{" "}
                            whitelabel.feednatic.com
                          </p>
                          <p>
                            <strong>TTL:</strong> 3600
                          </p>
                        </div>
                        
                        <p className="mt-4">
                          After adding the DNS records, your pages will
                          be available at:{" "}
                        </p>
                        <ul className="mt-2 list-disc pl-5">
                          <li>
                            <strong>Feedback Portal:</strong>{" "}
                            <code className="text-sm">feedback.{field.value}</code>
                          </li>
                          <li>
                            <strong>Help Desk:</strong>{" "}
                            <code className="text-sm">help.{field.value}</code>
                          </li>
                        </ul>
                        <p className="mt-2 text-sm text-muted-foreground">
                          Note: DNS changes can take up to 48 hours to propagate
                          globally.
                        </p>
                      </AlertDescription>
                    </Alert>
                  )}
                </FormItem>
              )}
            />
          )}

          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Updating..." : "Update Company"}
          </Button>
        </form>
      </Form>

      {/* Danger Zone - Delete Company */}
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>
            Permanently delete your company and account. This action cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={isDeleting}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Company & Account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription className="space-y-2">
                  <p>This action cannot be undone. This will permanently delete:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Your company and all its data</li>
                    <li>All feedback, boards, and comments</li>
                    <li>All team members and invitations</li>
                    <li>All knowledge base articles</li>
                    <li>All changelogs and subscribers</li>
                    <li>All roadmaps and statuses</li>
                    <li>All chat messages and contacts</li>
                    <li>Your user profile and account</li>
                  </ul>
                  <p className="font-semibold mt-4">
                    You will be logged out and your account will be permanently deleted.
                  </p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    handleDeleteCompany();
                  }}
                  disabled={isDeleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDeleting ? "Deleting..." : "Delete Everything"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>

      <UpgradePrompt
        open={showUpgradePrompt}
        onOpenChange={setShowUpgradePrompt}
        message={upgradeMessage}
      />
    </div>
  );
};

export default CompanySettings;
